import type {
  DataGridColumnOrderState,
  DataGridFilterState,
  DataGridLeafColumn,
  DataGridSortingState,
} from '@ibsys/kit';
import { DataGrid } from '@ibsys/kit';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserUrlLogItem } from '@/entities/user-url-log';
import { loadUserOptions, resolveUserOptions } from '@/entities/users';
import { getFullName } from '@/shared/lib';
import { formatHumanDate } from '@/shared/lib/utils/date';

type Props = {
  totalCount: number;
  pageSize: number;
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<UserUrlLogItem[]>;
  cacheVersion: string;
  sorting: DataGridSortingState;
  filters: DataGridFilterState;
  columnOrder: DataGridColumnOrderState;
  onSortingChange: (sorting: DataGridSortingState) => void;
  onFiltersChange: (filters: DataGridFilterState) => void;
  onColumnOrderChange: (order: DataGridColumnOrderState) => void;
  /** Контрол очистки лога; показывается в шапке только при наличии права ADMIN_USER_LOG. */
  clearControl?: ReactNode;
};

/**
 * Презентер лога URL: заголовок «Пользовательский лог» и DataGrid
 * (тот же грид, что и в диалогах) в lazy-режиме — бесконечная прокрутка с догрузкой порций,
 * серверная сортировка по клику на заголовок, фильтры по колонкам (селекторы пользователя
 * и профиля работы от чужого имени, текст-шаблон по URL, интервал даты/времени)
 * и DnD-перестановка колонок.
 */
export const UserUrlLogTable: FC<Props> = ({
  totalCount,
  pageSize,
  fetchPage,
  cacheVersion,
  sorting,
  filters,
  columnOrder,
  onSortingChange,
  onFiltersChange,
  onColumnOrderChange,
  clearControl,
}) => {
  const { t, i18n } = useTranslation(['userUrlLog']);

  const columns = useMemo<DataGridLeafColumn<UserUrlLogItem>[]>(
    () => [
      {
        id: 'user',
        header: t('table.user'),
        accessor: (row) => getFullName(row.userFirstName, row.userLastName),
        size: 240,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'select', loadOptions: loadUserOptions, resolveOptions: resolveUserOptions },
      },
      {
        id: 'impersonatedUser',
        header: t('table.impersonatedUser'),
        // Вне режима работы от чужого имени профиля нет — ячейка остаётся пустой
        accessor: (row) => getFullName(row.impersonatedUserFirstName, row.impersonatedUserLastName),
        size: 240,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'select', loadOptions: loadUserOptions, resolveOptions: resolveUserOptions },
      },
      {
        id: 'url',
        header: t('table.url'),
        accessor: 'url',
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
        wrap: true,
      },
      {
        id: 'createdAt',
        header: t('table.createdAt'),
        accessor: 'createdAt',
        size: 220,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'timestamp-range' },
        // Момент события показываем в локальном часовом поясе пользователя — тем же форматтером, что и грид диалогов
        cell: ({ row }) => formatHumanDate(i18n, row.createdAt, { withTime: true }),
      },
    ],
    [t, i18n],
  );

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="text-2xl font-semibold">{t('title')}</h1>
        {clearControl && <div className="ml-auto">{clearControl}</div>}
      </div>
      <div className="flex-1 min-h-0" data-testid="user-url-log-table">
        <DataGrid
          className="h-full w-full"
          columns={columns}
          dataSource={{
            mode: 'lazy',
            totalCount,
            pageSize,
            fetchPage,
            cacheVersion,
          }}
          getRowId={(row) => String(row.id)}
          sorting={sorting}
          onSortingChange={onSortingChange}
          filters={filters}
          onFiltersChange={onFiltersChange}
          enableColumnReordering
          columnOrder={columnOrder}
          onColumnOrderChange={onColumnOrderChange}
        />
      </div>
    </>
  );
};
